import { FC, useMemo, useState, useCallback, ReactElement, createRef } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { DefinedUseQueryResult } from '@tanstack/react-query'
import { FormApi } from 'final-form'

import {
  Tabs,
  SafetyCasePopup,
  SafetyCaseHazardPopup,
  SelectOption,
  ISafetyCaseValues,
  ISafetyCaseUpsertValues,
} from '../../../components'
import ContentMapLayout from '../../../hoc/content-map-layout'
import Map from '../../../components/map/map'
import MarkerLayout from '../../../components/map/marker-layout'
import PolygonLayout from '../../../components/map/polygon-layout'
import SafetyCaseInfo from './safety-case-info'
import SafetyCaseHazards from './safety-case-hazards'
import SafetyCaseActions from './safety-case-actions'

import { getGeoJson } from '../../../util'
import { useSafeQueryParams } from '../../../hooks'
import { caseQueryParamsToParams } from './safety-cases-info-utils'

import { ISafetyCase, IGeoJson, IRefs, ISafetyCaseHazard } from '../../../interfaces'

type SafetyCasePageBuilderType = {
  hasWriteAccess?: boolean
  handleSaveSafetyCase: (safetyCaseValues: ISafetyCaseValues) => void
  handleUpdateSafetyReportInternalAnswer?: (payload: { id: number; internalAnswer: string }) => void
  safetyCaseQuery?: DefinedUseQueryResult<ISafetyCase | undefined, Error>
  setHeaderControls: (headerControls?: ReactElement) => void
  saveHeaderControls: ReactElement
  setSaveSafetyCaseForm?: (saveSafetyCaseForm: FormApi<ISafetyCaseValues>) => void
  saveSafetyCaseInitialValues?: ISafetyCaseUpsertValues
  setSaveSafetyCaseValues?: (safetyCaseUpsertValues: ISafetyCaseUpsertValues) => void
  isSaveFormDisabled?: boolean
}

const SafetyCasePageBuilder: FC<SafetyCasePageBuilderType> = props => {
  const {
    hasWriteAccess,
    handleSaveSafetyCase,
    handleUpdateSafetyReportInternalAnswer,
    safetyCaseQuery,
    setHeaderControls,
    saveHeaderControls,
    setSaveSafetyCaseForm,
    saveSafetyCaseInitialValues,
    setSaveSafetyCaseValues,
    isSaveFormDisabled,
  } = props

  const location = useLocation()

  const navigate = useNavigate()

  const safetyCase = safetyCaseQuery?.data

  const additionalSaveSafetyCaseOptions = useMemo(
    () => ({
      getStatusesOptions: (options: SelectOption[]) => {
        if (!safetyCase) {
          return options
        }

        if (options.find(option => option.value === safetyCase.parentStatus.id)) {
          return options
        }

        return [
          { value: safetyCase.parentStatus.id, label: safetyCase.parentStatus.name || 'Unknown Status' },
          ...options,
        ]
      },
    }),
    [safetyCase],
  )

  const isSafetyCaseFilled = useMemo(() => Boolean(safetyCaseQuery?.data?.name), [safetyCaseQuery?.data])

  const routes = useMemo(
    () => [
      { to: 'info', label: 'General', allowedClaims: '*', replace: true },
      {
        to: 'hazards',
        label: `Hazards (${safetyCaseQuery?.data?.hazards?.length})`,
        disabled: !isSafetyCaseFilled,
        allowedClaims: '*',
        replace: true,
        search: location.search,
      },
      {
        to: 'activity',
        label: `Activity (${safetyCaseQuery?.data?.actions?.length})`,
        disabled: !isSafetyCaseFilled,
        allowedClaims: '*',
        replace: true,
        search: location.search,
      },
    ],
    [
      isSafetyCaseFilled,
      location.search,
      safetyCaseQuery?.data?.hazards?.length,
      safetyCaseQuery?.data?.actions?.length,
    ],
  )

  const [hazardActiveGeoJsonId, setHazardActiveGeoJsonId] = useState<number | undefined>(undefined)
  const [hazardInfoGeoJsonId, setHazardInfoGeoJsonId] = useState<number | undefined>(undefined)

  const hazardsRefs = useMemo(
    () =>
      ((safetyCaseQuery?.data?.hazards || []) as ISafetyCaseHazard[]).reduce<IRefs>((acc, value) => {
        if (value.id) {
          acc[value.id] = createRef()
        }

        return acc
      }, {}),
    [safetyCaseQuery?.data?.hazards],
  )

  const { params, setParams } = useSafeQueryParams(caseQueryParamsToParams)

  const safetyCaseHazardsPagination = useMemo(() => params.hazards.pagination, [params.hazards.pagination])

  const setSafetyCaseHazardsPagination = useCallback(
    (values: { [key: string]: number }) => {
      setParams({
        hazards: {
          pagination: {
            ...safetyCaseHazardsPagination,
            ...values,
          },
        },
      })
    },
    [setParams, safetyCaseHazardsPagination],
  )

  const { markersGeoJson: hazardsMarkersGeoJson, polygonsGeoJson: hazardsPolygonsGeoJson } = getGeoJson(
    safetyCaseQuery?.data?.hazards || [],
    (hazard: ISafetyCaseHazard) =>
      hazard.geoJson && {
        ...hazard.geoJson,
        properties: { ...(hazard.geoJson.properties || {}), id: hazard.id },
      },
  )

  const activeHazardId = hazardActiveGeoJsonId

  const handleNavigateToSafetyCaseHazard = useCallback(
    (hazard?: ISafetyCaseHazard) => {
      if (!safetyCase?.id || !hazard?.id) {
        return
      }

      navigate(`/safety-cases/${safetyCase.id}/hazards/${hazard.id}`)
    },
    [navigate, safetyCase?.id],
  )

  const renderHazardPopUp = useCallback(
    (geoJson: IGeoJson) => {
      const hazard = safetyCaseQuery?.data?.hazards?.find(
        (hazard: ISafetyCaseHazard) => hazard.id === geoJson?.properties?.id,
      )

      return (
        <SafetyCaseHazardPopup
          safetyCaseHazard={hazard}
          onNavigateToSafetyCaseHazard={handleNavigateToSafetyCaseHazard}
        />
      )
    },
    [safetyCaseQuery?.data?.hazards, handleNavigateToSafetyCaseHazard],
  )

  const safetyCaseActionsPagination = useMemo(() => params.actions.pagination, [params.actions.pagination])

  const setSafetyCaseActionsPagination = useCallback(
    (values: { [key: string]: number }) => {
      setParams({
        actions: {
          pagination: {
            ...safetyCaseActionsPagination,
            ...values,
          },
        },
      })
    },
    [setParams, safetyCaseActionsPagination],
  )

  const { markersGeoJson: safetyCaseMarkersGeoJson, polygonsGeoJson: safetyCasePolygonsGeoJson } = getGeoJson(
    safetyCase?.hazards || [],
    hazard => hazard.geoJson,
  )

  const renderSafetyCasePopUp = useCallback(
    () => <SafetyCasePopup safetyCase={safetyCase} />,
    [safetyCase],
  )

  const [safetyCaseActiveGeoJsonId, setSafetyCaseActiveGeoJsonId] = useState<number | undefined>(undefined)
  const [safetyCaseInfoGeoJsonId, setSafetyCaseInfoGeoJsonId] = useState<number | undefined>(undefined)

  return (
    <ContentMapLayout>
      {{
        content: (
          <div className="d-flex flex-column w-100">
            {routes.filter(route => !route.disabled).length > 1 && (
              <Tabs routes={routes.filter(route => !route.disabled)} />
            )}

            <Routes>
              <Route index element={<Navigate to="info" replace />} />

              <Route
                path="/info"
                element={
                  <SafetyCaseInfo
                    hasWriteAccess={hasWriteAccess}
                    setHeaderControls={setHeaderControls}
                    additionalSaveSafetyCaseOptions={additionalSaveSafetyCaseOptions}
                    handleSaveSafetyCase={handleSaveSafetyCase}
                    handleUpdateSafetyReportInternalAnswer={handleUpdateSafetyReportInternalAnswer}
                    saveHeaderControls={saveHeaderControls}
                    setSaveSafetyCaseForm={setSaveSafetyCaseForm}
                    saveSafetyCaseInitialValues={saveSafetyCaseInitialValues}
                    setSaveSafetyCaseValues={setSaveSafetyCaseValues}
                    isSaveFormDisabled={isSaveFormDisabled}
                    safetyReport={safetyCase?.safetyReport}
                  />
                }
              />

              {safetyCase && isSafetyCaseFilled && (
                <Route
                  path="/hazards"
                  element={
                    <SafetyCaseHazards
                      hasWriteAccess={hasWriteAccess}
                      safetyCaseQuery={safetyCaseQuery}
                      setHeaderControls={setHeaderControls}
                      hazardsPagination={safetyCaseHazardsPagination}
                      setHazardsPagination={setSafetyCaseHazardsPagination}
                      hazardsRefs={hazardsRefs}
                      activeHazardId={activeHazardId}
                    />
                  }
                />
              )}

              {safetyCase && isSafetyCaseFilled && (
                <Route
                  path="/activity"
                  element={
                    <SafetyCaseActions
                      hasWriteAccess={hasWriteAccess}
                      safetyCaseQuery={safetyCaseQuery}
                      setHeaderControls={setHeaderControls}
                      safetyCaseActionsPagination={safetyCaseActionsPagination}
                      setSafetyCaseActionsPagination={setSafetyCaseActionsPagination}
                    />
                  }
                />
              )}

              <Route path="*" element={null} />
            </Routes>
          </div>
        ),
        map: (
          <Map>
            <MarkerLayout
              activeId={safetyCaseActiveGeoJsonId}
              setActiveId={setSafetyCaseActiveGeoJsonId}
              infoId={safetyCaseInfoGeoJsonId}
              setInfoId={setSafetyCaseInfoGeoJsonId}
              geoJson={safetyCaseMarkersGeoJson}
              renderPopUp={renderSafetyCasePopUp}
            />

            <PolygonLayout
              activeId={safetyCaseActiveGeoJsonId}
              setActiveId={setSafetyCaseActiveGeoJsonId}
              infoId={safetyCaseInfoGeoJsonId}
              setInfoId={setSafetyCaseInfoGeoJsonId}
              geoJson={safetyCasePolygonsGeoJson}
              renderPopUp={renderSafetyCasePopUp}
            />

            <MarkerLayout
              activeId={hazardActiveGeoJsonId}
              setActiveId={setHazardActiveGeoJsonId}
              infoId={hazardInfoGeoJsonId}
              setInfoId={setHazardInfoGeoJsonId}
              geoJson={hazardsMarkersGeoJson}
              renderPopUp={renderHazardPopUp}
            />

            <PolygonLayout
              activeId={hazardActiveGeoJsonId}
              setActiveId={setHazardActiveGeoJsonId}
              infoId={hazardInfoGeoJsonId}
              setInfoId={setHazardInfoGeoJsonId}
              geoJson={hazardsPolygonsGeoJson}
              renderPopUp={renderHazardPopUp}
            />
          </Map>
        ),
      }}
    </ContentMapLayout>
  )
}

export default SafetyCasePageBuilder
